/**
 * What actually went wrong when a login code was refused.
 *
 * Reported from a device: the right code, rejected every time, on mobile only.
 * The code was never the problem — the app had lost track of WHICH EMAIL the
 * code belonged to (Android reclaims a backgrounded app, and reading your email
 * is the one thing this screen requires you to leave for), so `verify()` threw
 * before it made a request. The screen's `catch` printed the same sentence it
 * printed for everything else:
 *
 *     "That code did not work. Try again."
 *
 * Which was false, and — worse — unactionable. Six distinct failures wore one
 * face: a wrong code, an expired code, a rate limit, a 400 from a five-digit
 * code, a server error, and a lost session that no retry could ever fix. That
 * one sentence is why this took days to find; from the outside every one of
 * them looked like the user mistyping.
 *
 * Classified structurally rather than with `instanceof`, so this stays a leaf
 * module with no imports and can be driven under node like `chat-follow.ts`.
 * `api/client.ts` pulls in react-native and cannot be loaded in a test at all,
 * which is exactly the reason `ApiError` was moved out of it.
 */

/**
 * Where a suspended person can argue. The only address that reaches us.
 *
 * A hard block makes "contact support" circular unless it names somewhere: they
 * cannot sign in, so nothing in the app is reachable to them — not Help, not
 * the in-app contact form, not an address behind a session. This is the whole
 * redress route, which is why it is shown as something tappable rather than
 * left inside a sentence.
 *
 * `/support` redirects to the landing page's contact form, which posts to the
 * unauthenticated `POST /support/public`. The alias exists because the landing
 * page's catch-all route was otherwise serving the HOMEPAGE for this address.
 *
 * Production in every build, deliberately: somebody suspended on a staging
 * build still needs the real form. `OPNCUE_SHARE_URL` on the home screen is
 * hard-coded for the same reason.
 *
 * Here rather than in a config module because this file has NO imports on
 * purpose — see the note above — and the address has to travel with the
 * classification that decides when to show it.
 */
export const SUPPORT_URL = "https://opncue.com/support";

export type VerifyFailure =
  /** We do not know which address the code was sent to. Retrying cannot help. */
  | "lost"
  /** The server looked at the code and said no. */
  | "invalid"
  /** The server has already forgotten this code. A resend is the way out. */
  | "expired"
  /** Burned by wrong guesses. Also only fixable by a resend, but for a reason
   *  the user needs told plainly — otherwise they retype the same code. */
  | "burned"
  /** Fewer digits than the server accepts — it never reached the comparison. */
  | "length"
  /** Too many attempts from here, for now. */
  | "rate_limited"
  /** Ours, not theirs. */
  | "service"
  /** The request never got there. */
  | "offline"
  /**
   * The account exists, the code was right, and we will not let them in.
   *
   * Not a verdict on the code, and not something a resend can fix — so it must
   * not wear any of the sentences above. The person needs to be told plainly
   * that the account is suspended and how to argue with it, which is also what
   * DSA 17 requires of us.
   */
  | "suspended";

/** The shape of the errors this app throws, without importing any of them. */
type ErrorLike = {
  name?: unknown;
  status?: unknown;
  message?: unknown;
  /** The parsed response body, which `ApiError` carries. */
  body?: unknown;
};

export function verifyFailure(err: unknown): VerifyFailure {
  const like = (err ?? {}) as ErrorLike;
  const name = typeof like.name === "string" ? like.name : "";
  const status = typeof like.status === "number" ? like.status : null;
  const message = typeof like.message === "string" ? like.message : "";

  if (name === "MissingPendingEmailError") return "lost";
  if (name === "TimeoutError") return "offline";

  if (status === null) {
    // No status at all: a network reject, or something of ours that threw.
    // Either way it is not a verdict on the code, so it must not read as one.
    return "service";
  }
  // Read the code, not the status, and not the sentence.
  //
  // A bare 403 is ambiguous: on web, sign-in goes to `/auth/web/verify`, which
  // sits behind `WebOriginGuard`, and that throws 403 for an origin or header
  // problem. Classifying every 403 as a suspension would tell a user their
  // account was suspended because a deployment got its origins wrong.
  //
  // Matching the English sentence would be worse in a different way — it breaks
  // the day that sentence is translated, which is the whole direction this
  // codebase is moving.
  if (status === 403 && suspensionCode(like.body)) return "suspended";

  if (status === 400) return "length";
  if (status === 429) return "rate_limited";
  if (status >= 500) return "service";
  if (status === 401) {
    // The server says 401 for three different things, and they need three
    // different sentences: try again, send a new one, and "you have used up
    // this code's attempts". Collapsing the last into "expired" leaves the user
    // retyping a code that will never be accepted again.
    if (/too many incorrect/i.test(message)) return "burned";
    return /expired|not found/i.test(message) ? "expired" : "invalid";
  }
  return "invalid";
}

/** `true` when the body is the server's suspension refusal. */
function suspensionCode(body: unknown): boolean {
  return (
    typeof body === "object" &&
    body !== null &&
    (body as { code?: unknown }).code === "account_suspended"
  );
}
